import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, ShieldCheck, TreeStructure, Receipt, DownloadSimple, Eye, CaretDown } from "@phosphor-icons/react";

const money = (v) => `€${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AccountantPortal() {
  const [clients, setClients] = useState(null);
  const [clientId, setClientId] = useState("");
  const [overview, setOverview] = useState(null);
  const [grants, setGrants] = useState([]);

  useEffect(() => {
    api.get("/accountant/clients").then((r) => {
      setClients(r.data.clients || []);
      if (r.data.clients?.length) setClientId(r.data.clients[0].client_user_id);
    }).catch(() => setClients([]));
  }, []);

  const loadClient = useCallback(async (cid) => {
    if (!cid) return;
    setOverview(null);
    const [ov, g] = await Promise.all([
      api.get(`/accountant/client/${cid}/overview`),
      api.get(`/accountant/client/${cid}/grants`),
    ]);
    setOverview(ov.data); setGrants(g.data.grants || []);
  }, []);
  useEffect(() => { if (clientId) loadClient(clientId); }, [clientId, loadClient]);

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (clients === null) return <div className="text-white/40 text-sm">Loading…</div>;

  if (clients.length === 0) return (
    <div className="space-y-6">
      <div><div className="eyebrow mb-2">Client Portal</div><h1 className="font-display text-4xl">Your client portal</h1></div>
      <div className="glass rounded-2xl p-12 text-center">
        <Buildings size={40} weight="duotone" className="text-white/40 mx-auto mb-4" />
        <div className="font-display text-lg mb-2">No clients yet</div>
        <p className="text-white/55 text-sm max-w-md mx-auto">When a founder invites you as their accountant, their company appears here with read-only access to grants, compliance and exports.</p>
      </div>
    </div>
  );

  const d = overview?.dashboard || {};
  const c = overview?.compliance || {};
  const rw = overview?.runway || {};

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Client Portal</div>
          <h1 className="font-display text-4xl">{overview?.client?.name || "Client"}</h1>
          <div className="flex items-center gap-2 mt-2 text-xs text-white/50"><Eye size={14} /> Read-only view · every access is logged</div>
        </div>
        <div className="relative">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Acting for</label>
          <div className="relative">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="client-switcher" className="pr-9 appearance-none">
              {clients.map((cl) => <option key={cl.client_user_id} value={cl.client_user_id}>{cl.company_name}</option>)}
            </select>
            <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          </div>
        </div>
      </div>

      {!overview ? <div className="text-white/40 text-sm">Loading client…</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={ShieldCheck} label="Compliance score" value={`${d.compliance_score ?? 100}%`} />
            <Kpi icon={TreeStructure} label="Grants" value={d.grants_count ?? 0} hint={`${money(d.total_awarded)} awarded`} />
            <Kpi icon={Receipt} label="Receipts" value={d.receipts_count ?? 0} hint={`${d.receipts_flagged ?? 0} flagged`} />
            <Kpi icon={ShieldCheck} label="Runway" value={rw.runway_months == null ? "∞" : `${rw.runway_months} mo`} hint={rw.zero_date || "—"} />
          </div>

          <div className="glass rounded-2xl p-6" data-testid="portal-exports">
            <div className="eyebrow mb-4">Exports (read-only)</div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => download(`/accountant/client/${clientId}/export/sage.csv`, "sage.csv")} className="btn-ghost text-sm" data-testid="export-sage"><DownloadSimple size={15} /> Sage CSV</button>
              <button onClick={() => download(`/accountant/client/${clientId}/export/xero.csv`, "xero.csv")} className="btn-ghost text-sm" data-testid="export-xero"><DownloadSimple size={15} /> Xero CSV</button>
            </div>
          </div>

          <div className="glass rounded-2xl p-6" data-testid="portal-grants">
            <div className="eyebrow mb-4">Grants</div>
            {grants.length === 0 ? <div className="text-white/40 text-sm">No grants for this client.</div> : (
              <table className="data"><thead><tr><th>Grant</th><th>Programme</th><th>Awarded</th><th>Spent</th><th>Report</th></tr></thead>
                <tbody>
                  {grants.map((g) => (
                    <tr key={g.grant_id} data-testid={`portal-grant-${g.grant_id}`}>
                      <td>{g.name}</td><td className="text-white/60">{g.programme}</td>
                      <td className="mono">{money(g.total_awarded)}</td><td className="mono text-[#FFB020]">{money(g.spent)}</td>
                      <td className="text-right"><button onClick={() => download(`/accountant/client/${clientId}/export/grant/${g.grant_id}.csv`, `grant-${g.name}.csv`)} className="btn-ghost text-xs" data-testid={`export-grant-${g.grant_id}`}><DownloadSimple size={13} /> Utilisation</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={`portal-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/50"><Icon size={16} /><span className="text-[10px] uppercase tracking-[0.2em]">{label}</span></div>
      <div className="mono text-2xl mt-2">{value}</div>
      {hint ? <div className="text-[11px] text-white/45 mt-1">{hint}</div> : null}
    </div>
  );
}
